import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Edit } from "../../icons/Edit-Icon.svg";
import { useTranslation } from "react-i18next";
import { getReq } from "../../configs/ApiUrl";
import { FurnitureSearch } from "../../App.js";
import AlwaysTop from "./util/AlwaysTop.js";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TablePagination,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";

function AdminProductTable() {
  const { t } = useTranslation(["admin", "altText", "tables", "tooltips"]);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useContext(FurnitureSearch);
  const [loading, setLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});
  const [pendingProducts, setPendingProducts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const visibleCards = 3;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    getAllPendingProducts();
    AlwaysTop();
  }, []);

  useEffect(() => {
    pendingProducts.forEach((product) => initializeLoadingState(product._id));
  }, [pendingProducts]);

  const initializeLoadingState = (productId) => {
    setLoadingStates((prevState) => ({ ...prevState, [productId]: false }));
  };

  const getAllPendingProducts = async () => {
    try {
      setLoading(true);
      const allProductsResp = await getReq("/product/all");
      if (allProductsResp?.allProducts) {
        const filteredProducts = allProductsResp.allProducts.filter(
          (product) => product.status === "pending"
        );
        setPendingProducts(filteredProducts);
      } else {
        setPendingProducts([]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box className='w-full h-full'>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}>
        {loading ? (
          <Typography
            variant='h6'
            className='text-center text-gray-500 mt-10'>
            Loading...
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ width: "100%", height: "100%", boxShadow: 3 }}>
            {isMobile ? (
              <Box className='flex flex-col gap-4 p-2'>
                {pendingProducts
                  .slice(currentIndex, currentIndex + visibleCards)
                  .map((product) => (
                    <motion.div
                      key={product._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{
                        scale: 1.01,
                        backgroundColor: "#f5f5f5",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                      }}
                      className='border rounded p-3 shadow-sm'>
                      <Box className='flex items-center gap-3'>
                        <img
                          src={product.imageNames[0]}
                          alt={t("altText:firstImage")}
                          className='w-20 h-20 object-cover rounded'
                        />
                        <Box>
                          <Typography
                            variant='subtitle1'
                            fontWeight={600}>
                            {product.title.length > 20
                              ? product.title.substring(0, 15) + "..."
                              : product.title}
                          </Typography>
                        </Box>
                      </Box>
                      <Box className='flex justify-end mt-2 gap-1'>
                        <IconButton
                          onClick={() => {
                            setSearchTerm({
                              ...searchTerm,
                              adminSearch: [product],
                            });
                            navigate("/admin-product-view");
                          }}>
                          <Edit className='h-5 w-5' />
                          <span className='ml-2 text-sm font-bold'>
                            Review Submission
                          </span>
                        </IconButton>
                      </Box>
                    </motion.div>
                  ))}
              </Box>
            ) : (
              <Table size='medium'>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>{t("tables:image")}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{t("tables:product")}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{t("tables:status")}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{t("tables:actions")}</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingProducts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product) => (
                      <motion.tr
                        key={product._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}>
                        <TableCell>
                          <Box
                            sx={{
                              width: 75,
                              height: 60,
                              overflow: "hidden",
                              borderRadius: 2,
                            }}>
                            <img
                              src={product.imageNames[0]}
                              alt={t("altText:firstImage")}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600}>
                            {product.title.length > 20
                              ? product.title.substring(0, 15) + "..."
                              : product.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              product.status
                                ? product.status.charAt(0).toUpperCase() +
                                  product.status.slice(1)
                                : ""
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t("tooltips:reviewSubmission")}>
                            <IconButton
                              onClick={() => {
                                setSearchTerm({
                                  ...searchTerm,
                                  adminSearch: [product],
                                });
                                navigate("/admin-product-view");
                              }}>
                              <Edit className='h-5 w-5' />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </motion.tr>
                    ))}
                </TableBody>
              </Table>
            )}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <TablePagination
                component='div'
                count={pendingProducts.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </TableContainer>
        )}
      </motion.div>
    </Box>
  );
}

export default AdminProductTable;
